import msSc900 from "../assets/badges/microsoft-sc-900.svg";
import googleCybersecurity from "../assets/badges/google-cybersecurity.png";
import isc2Candidate from "../assets/badges/isc2-candidate.png";
import mongodbAiAgents from "../assets/badges/mongodb-ai-agents.png";

/**
 * Professional-face content (Audience B in the marketing brief): Olga hired
 * directly, freelance or interim.
 *
 * This page is optimised for a hiring decision, not for browsing — headline,
 * availability, proof, then depth.
 *
 * ⚠️ CONFIRM BEFORE LAUNCH — the fields marked NEEDS-CONFIRMATION are drawn
 * from the business plan rather than from a stated preference. Check them.
 */

export const HEADLINE = {
  name: "Olga Molina",
  /** Single professional title, per §3 of the marketing brief. */
  title: "Product designer & full-stack developer",
  specialism: "specialised in security and compliance",
  summary:
    "I join teams that need design and build capacity without giving up on security, GDPR and accessibility — from UX research through to shipped front-end, with certified security fundamentals on top.",
} as const;

/**
 * The practical facts shown on the hero card. Deliberately NO availability
 * signal — no status, capacity or start date — so the page never tells a visitor
 * whether Olga is currently free or how booked she is.
 */
export const AVAILABILITY = {
  /** NEEDS-CONFIRMATION: no rate is stated anywhere in the brief or plan. */
  rate: "Day rate on request",
  location: "Weesp, Netherlands",
  remote: "Remote across Europe · hybrid or on-site in the Randstad · willing to relocate",
  entity: "Dutch ZZP / eenmanszaak, or open to payroll positions",
} as const;

/**
 * The featured way to hire — a permanent, salaried role. Framed as the best
 * value because it's the only option that isn't time-boxed: it rolls all three
 * models below into one committed teammate. Rendered above the grid.
 */
export const PAYROLL_ENGAGEMENT = {
  title: "In-house",
  badge: "Best value",
  body: "Skip the scoping. Bring me onto the team as a permanent, salaried hire — design, full-stack build and security-by-default in one person who's actually yours. Everything Interim, Project and Advisory do, rolled into a single teammate who's all-in, every day.",
} as const;

export const ENGAGEMENT_MODELS = [
  {
    title: "Interim",
    body: "Embedded in your team for a fixed period. Design and build, standups included, your tools and your process.",
  },
  {
    title: "Project",
    body: "A scoped deliverable with a fixed outcome: a product design, a front-end build, or an AI-first MVP.",
  },
  {
    title: "Advisory",
    body: "Shorter engagements: security, GDPR/AVG and WCAG review of something your team already built.",
  },
] as const;

export const LANGUAGES = [
  { name: "Spanish", level: "Native" },
  { name: "English", level: "Fluent" },
  { name: "Dutch", level: "A2 — elementary" },
] as const;

export const CREDENTIALS = [
  {
    name: "Microsoft SC-900",
    detail: "Security, Compliance & Identity Fundamentals",
    href: "https://learn.microsoft.com/en-us/users/olgamolina-5685/credentials/a2188580b7a4d0ee",
  },
  {
    name: "Google Cybersecurity",
    detail: "Professional Certificate",
    href: "https://www.coursera.org/account/accomplishments/specialization/certificate/Y6Z0QBSU0F3S",
  },
] as const;

/**
 * Verified digital badges, rendered as the Certifications wall under Capabilities.
 *
 * The badge artwork is SELF-HOSTED (src/assets/badges) and each card links to the
 * issuer's own public verification page. We deliberately do NOT use Credly's
 * embed.js: it injects a third-party iframe on load, which would contradict the
 * cookie banner's "nothing loads unless you accept" promise on a page that sells
 * GDPR competence — and it renders nothing in the prerendered HTML.
 *
 * To refresh artwork: the Credly image lives at
 * https://images.credly.com/size/340x340/images/<image-id>/blob, findable from
 * https://api.credly.com/v1/obi/v2/badge_assertions/<badge-id>.
 */
export type Certification = {
  name: string;
  issuer: string;
  /** The issuer's public verification page for this specific badge. */
  verifyUrl: string;
  image: string;
  /** Accent used for the card's rule — reuses the brand palette. */
  accent: string;
};

export const CERTIFICATIONS: Certification[] = [
  {
    name: "Security, Compliance & Identity Fundamentals (SC-900)",
    issuer: "Microsoft Certified",
    verifyUrl:
      "https://learn.microsoft.com/api/credentials/share/en-gb/OlgaMolina-5685/A2188580B7A4D0EE?sharingId=20F8CDF770E70F42",
    image: msSc900,
    accent: "#256af4",
  },
  {
    name: "Google Cybersecurity Professional Certificate",
    issuer: "Google · Coursera",
    verifyUrl: "https://www.credly.com/badges/6b42ed36-dd07-4f0e-a710-b94c49543def/public_url",
    image: googleCybersecurity,
    accent: "#f42536",
  },
  {
    name: "ISC2 Candidate",
    issuer: "ISC2",
    verifyUrl: "https://www.credly.com/badges/3d987ed7-ec90-4c8e-b5c4-1a3fcd212470/public_url",
    image: isc2Candidate,
    accent: "#22c365",
  },
  {
    name: "Building AI Agents with MongoDB",
    issuer: "MongoDB",
    verifyUrl: "https://www.credly.com/badges/570653b1-3573-48a7-9360-61e004e38775/public_url",
    image: mongodbAiAgents,
    accent: "#01e12d",
  },
];

export const SKILL_GROUPS = [
  {
    title: "Design",
    items: ["UX research", "UI & design systems", "Information architecture", "Figma", "Adobe CC", "Brand identity"],
  },
  {
    title: "Front-end",
    items: ["React", "TypeScript", "Next.js", "Vue.js", "Tailwind", "HTML5 / CSS"],
  },
  {
    title: "Back-end",
    items: ["Node.js", "Python", "SQL", "MongoDB", "WordPress", "CI/CD"],
  },
  {
    title: "Security & compliance",
    items: ["Secure-by-design", "GDPR / AVG", "WCAG 2.2", "Threat modelling", "Hardening reviews", "Cookie & consent"],
  },
] as const;

/**
 * Role-targeted CVs — the full set of 10.
 *
 * The recruiter never sees this list. The CvFinder questionnaire (behind the
 * "Download CV" button) walks them from discipline → specialisation and hands
 * back exactly ONE CV, so a multidisciplinary profile never overwhelms a single
 * hiring manager. The mapping from answers to `key` lives in
 * components/profile/cvFinderData.ts.
 *
 * Source PDFs live in build/Q2 and are copied into /public/cv. Adding a role is
 * two steps: drop the PDF in public/cv, add a row here (and a leaf in
 * cvFinderData.ts so the questionnaire can reach it).
 */
export type CvKey =
  | "product-designer"
  | "design-engineer"
  | "product-designer-ai-first"
  | "product-engineer"
  | "frontend-developer"
  | "junior-software-engineer"
  | "ai-solutions-automation"
  | "customer-support"
  | "it-support"
  | "cybersecurity-analyst";

export type CvOption = {
  /** Stable id the questionnaire maps to. */
  key: CvKey;
  role: string;
  /** Who this CV is for, in the hiring manager's words. */
  forWhom: string;
  file: string;
  accent: string;
};

export const CV_OPTIONS: CvOption[] = [
  {
    key: "product-designer",
    role: "Product Designer",
    forWhom: "You need research, flows and UI — someone to own the product's shape.",
    file: "/cv/olga-molina-product-designer.pdf",
    accent: "#f42536",
  },
  {
    key: "design-engineer",
    role: "Design Engineer",
    forWhom: "You need one person who designs it and then actually ships the front-end.",
    file: "/cv/olga-molina-design-engineer.pdf",
    accent: "#256af4",
  },
  {
    key: "product-designer-ai-first",
    role: "Product Designer — AI First",
    forWhom: "You want AI-assisted delivery speed without losing design judgement.",
    file: "/cv/olga-molina-product-designer-ai-first.pdf",
    accent: "#7a4dff",
  },
  {
    key: "product-engineer",
    role: "Product Engineer",
    forWhom: "You need end-to-end delivery: front-end, back-end and the calls in between.",
    file: "/cv/olga-molina-product-engineer.pdf",
    accent: "#01e12d",
  },
  {
    key: "frontend-developer",
    role: "Frontend Developer",
    forWhom: "You have designs and need them built properly — accessible and secure.",
    file: "/cv/olga-molina-frontend-developer.pdf",
    accent: "#22c365",
  },
  {
    key: "junior-software-engineer",
    role: "Junior Software Engineer",
    forWhom: "You need general full-stack delivery: JavaScript, TypeScript, Python, React and Node.",
    file: "/cv/olga-molina-junior-software-engineer.pdf",
    accent: "#0ea5b5",
  },
  {
    key: "ai-solutions-automation",
    role: "AI Solutions & Automation",
    forWhom: "You need AI features or internal automation scoped, built and shipped.",
    file: "/cv/olga-molina-ai-solutions-automation.pdf",
    accent: "#ffc803",
  },
  {
    key: "customer-support",
    role: "Customer Support",
    forWhom: "You need chat & email support with real product understanding — bugs reproduced, not just logged.",
    file: "/cv/olga-molina-customer-support.pdf",
    accent: "#ff7a1a",
  },
  {
    key: "it-support",
    role: "Junior IT Support Engineer",
    forWhom: "You need a service desk that troubleshoots, documents and speaks plain language to users.",
    file: "/cv/olga-molina-it-support.pdf",
    accent: "#f59e0b",
  },
  {
    key: "cybersecurity-analyst",
    role: "Junior Cybersecurity Analyst",
    forWhom: "You need entry-level security: IAM, access control and threat-detection fundamentals, certified.",
    file: "/cv/olga-molina-cybersecurity-analyst.pdf",
    accent: "#e11d48",
  },
];

/** Look up a CV by its key — the questionnaire's single source of truth. */
export const getCvByKey = (key: CvKey): CvOption =>
  CV_OPTIONS.find((option) => option.key === key) ?? CV_OPTIONS[0];
